import { useMutation } from '@tanstack/react-query';
import { generateKeyBetween } from 'fractional-indexing';
import type { Product as ProductDTO } from '@setvi/shared/interfaces';
import { addQuoteV2SubitemMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2ItemWithTotalsResponse } from 'Services/Query/QuotesV2/Types';
import {
  findItemById,
  addProductsOptimistically,
  composeQuoteDTO,
  updateItemInTree
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

export interface AddSubitemVariables {
  itemId: string;
  products: ProductDTO[];
}

/**
 * Add sub-items (options/accessories) to a parent product via the
 * dedicated v2 sub-item endpoint. Supports adding multiple products
 * at once. Applies optimistic update immediately, reverts on error,
 * and invalidates on success.
 */
export const useAddSubitem = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = addQuoteV2SubitemMutation();

  return useMutation({
    mutationFn: async ({ itemId, products }: AddSubitemVariables) => {
      const items = cache.getItems() || [];
      const parentItem = findItemById(items, itemId);

      // Generate sequential fractional positions after last child
      const children = (parentItem as any)?.children || [];
      let lastPosition =
        children
          .map((c: any) => c.position as string | undefined)
          .filter(Boolean)
          .sort()
          .pop() ?? null;

      const calls = products.map(product => {
        const position = generateKeyBetween(lastPosition, null);
        lastPosition = position;

        const sell = product.price || product.sell || 0;
        const cost = product.customerCost || product.cost || 0;

        return mutationFn({
          quoteId,
          itemId,
          productId: product._id,
          name: product.name,
          sku: product.sku || '',
          quantity: product.quantity ?? 1,
          sell,
          cost,
          position,
          isFreeHand: false,
          labelType: 'option',
          customLabel: '',
          includeInTotalCost: false
        });
      });

      return Promise.all(calls);
    },

    onMutate: async ({ itemId, products }) => {
      await cache.cancel();

      const previousItems = cache.getItems();

      // Compute positions from pre-optimistic parent's children
      const parentItem = findItemById(previousItems || [], itemId);
      const children = (parentItem as any)?.children || [];
      let lastPos =
        children
          .map((c: any) => c.position as string | undefined)
          .filter(Boolean)
          .sort()
          .pop() ?? null;

      const composed = composeQuoteDTO(cache.getQuote(), previousItems);
      const updated = addProductsOptimistically(
        composed as any,
        products,
        itemId
      );

      // Attach positions to the newly added optimistic children
      if (updated) {
        const originalCount = children.length;
        const itemsWithPositions = updated.items.map((item: any) => {
          if (
            (item.id || item._id) === itemId &&
            item.children?.length > originalCount
          ) {
            return {
              ...item,
              children: item.children.map((c: any, idx: number) => {
                if (idx >= originalCount && !c.position) {
                  const pos = generateKeyBetween(lastPos, null);
                  lastPos = pos;
                  return { ...c, position: pos, isPricingFetching: true };
                }
                return c;
              })
            };
          }
          return item;
        });
        cache.setItems(() => itemsWithPositions);
      }

      // Collect temp IDs of optimistic children we just added
      const updatedParent = findItemById(cache.getItems() || [], itemId);
      const updatedChildren = (updatedParent as any)?.children || [];
      const tempIds = updatedChildren
        .slice(children.length)
        .map((c: any) => c.id || c._id)
        .filter(Boolean) as string[];

      return { previousItems, tempIds };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data, _variables, context) => {
      const responses = data as QuoteV2ItemWithTotalsResponse[];

      if (responses?.length && context?.tempIds?.length) {
        cache.setItems(old => {
          if (!old) return old;

          return responses.reduce((items, response, i) => {
            const serverItem = response?.item;
            const tempId = context.tempIds[i];
            if (!serverItem || !tempId) return items;

            return updateItemInTree(items, tempId, {
              _id: serverItem._id,
              id: serverItem._id,
              isPricingFetching: false
            });
          }, old);
        });

        // Update quote totals from the last response
        const lastResponse = responses[responses.length - 1];
        if (lastResponse?.totals) {
          cache.setQuote(old =>
            old
              ? {
                  ...old,
                  totalSell: lastResponse.totals.totalSell,
                  totalCost: lastResponse.totals.totalCost
                }
              : old
          );
        }
      }

      cache.invalidate();
    }
  });
};
